/**
 * Wire-mapping for the PAM resilience dashboard (OIDCPAM Gate-6 Phase 5e —
 * ResilienceDashboard.tsx). Also reused by PamOverview.tsx's System Health
 * card (same adminPAMResilienceStatus endpoint).
 *
 * AdminPAMResilienceStatusResp / AdminPAMSLODashboardResp are EMPTY
 * interfaces in the generated client (see gen/client/react/client.ts) — the
 * Go handlers (admin_resilience_stepup_server.go#AdminPAMResilienceStatus /
 * #AdminPAMSLODashboard) bypass the genapi response encoder entirely and
 * write the JSON body directly via writeResilienceJSON "to preserve the
 * exact wire shape" (their own doc comments), so the generator could not
 * infer a concrete response schema. The ACTUAL wire JSON uses snake_case
 * json tags (degraded/maintenance_mode/deps/checked_at for status;
 * tenant_id/outstanding_count/breached_count/events/as_of for SLO — see the
 * two handlers' anonymous response structs), and the generated client's
 * fromWire() converts EVERY response key snake_case->camelCase before
 * resolving (client.ts's blind convertKeys(value, snakeToCamel) boundary
 * mapper — this applies even to untyped/opaque responses, since it isn't
 * schema-aware). So the REAL runtime shape at this boundary is
 * WireResilienceStatus / WireSloDashboard below (camelCase), NOT the
 * hand-written snake_case types the pre-migration page cast to.
 *
 * LATENT BUG FIXED BY THIS MAPPING (flagged in the shared Gate-6 doc): the
 * pre-migration ResilienceDashboard.tsx did
 * `setStatus(data as unknown as PAMResilienceStatus)` where PAMResilienceStatus
 * was a SNAKE_CASE local type (maintenance_mode, checked_at) and
 * `setSlo(data as unknown as PAMSLODashboard)` (outstanding_count,
 * breached_count, events[].subject_id/revoked_at/slo_deadline, as_of) — none
 * of those snake_case fields exist on the real camelCase runtime object, so
 * every field except the ones with no case difference (degraded, breached,
 * kind, status, id) rendered `undefined`: the maintenance toggle ALWAYS
 * showed "Maintenance mode OFF" regardless of actual server state, and every
 * "Since" / "Checked at" timestamp plus the SLO table's Subject ID / SLO
 * deadline / "as of" columns rendered blank. This mapping (WireX ->
 * @devnw/cyber-iam/iam/types camelCase props) fixes all of the above.
 */
import type {
  ResilienceStatus as IamResilienceStatus,
  SloDashboard as IamSloDashboard,
  DependencyHealth,
} from '@devnw/cyber-iam/iam/types'

/** The real (camelCase) runtime shape of one AdminPAMResilienceStatusResp dep. */
export interface WireDependencySnapshot {
  kind: string
  status: DependencyHealth
  since: string
  reason: string
}

/** The real (camelCase) runtime shape of AdminPAMResilienceStatusResp. */
export interface WireResilienceStatus {
  degraded: boolean
  maintenanceMode: boolean
  deps: WireDependencySnapshot[]
  checkedAt: string
}

/** The real (camelCase) runtime shape of one AdminPAMSLODashboardResp event. */
export interface WireSloEvent {
  id: string
  kind: string
  subjectId: string
  revokedAt: string
  sloDeadline: string
  propagatedAt?: string
  breached: boolean
}

/** The real (camelCase) runtime shape of AdminPAMSLODashboardResp. */
export interface WireSloDashboard {
  tenantId: string
  outstandingCount: number
  breachedCount: number
  events: WireSloEvent[]
  asOf: string
}

/** HOST-COMPUTED display formatting — the library never parses dates. */
function fmtDate(iso?: string): string | undefined {
  return iso ? new Date(iso).toLocaleString() : undefined
}

export function toIamResilienceStatus(
  w: WireResilienceStatus
): IamResilienceStatus {
  return {
    degraded: w.degraded,
    maintenanceMode: w.maintenanceMode,
    deps: (w.deps ?? []).map((d) => ({
      kind: d.kind,
      status: d.status,
      since: fmtDate(d.since),
      reason: d.reason,
    })),
    checkedAt: fmtDate(w.checkedAt),
  }
}

export function toIamSloDashboard(w: WireSloDashboard): IamSloDashboard {
  return {
    outstandingCount: w.outstandingCount,
    breachedCount: w.breachedCount,
    events: (w.events ?? []).map((ev) => ({
      id: ev.id,
      kind: ev.kind,
      subjectId: ev.subjectId,
      breached: ev.breached,
      revokedAt: fmtDate(ev.revokedAt),
      sloDeadline: fmtDate(ev.sloDeadline),
    })),
    asOf: fmtDate(w.asOf),
  }
}
